'use client';

import { useCallback } from 'react';
import type { CreateItemInput } from '../wishlist-proxy-client';
import { useWishlistMutations } from './use-wishlist-mutations';
import { useWishlistSafe } from './use-wishlist-safe';

/**
 * Hook that handles adding items to wishlist without forcing them into lists/boards.
 * Items are added to "all items" and only placed in boards when explicitly chosen by the user.
 */
export function useAddToWishlist() {
  const wishlistContext = useWishlistSafe();
  const { addItemMutation } = useWishlistMutations();

  const addToWishlist = useCallback(
    async (item: Omit<CreateItemInput, 'listId'>) => {
      if (!wishlistContext) {
        throw new Error('Wishlist not available');
      }

      const { loading } = wishlistContext;

      // Wait for wishlist to load
      if (loading) {
        throw new Error('Wishlist still loading');
      }

      // Add item without listId - it goes to "all items" by default
      const itemWithoutList: CreateItemInput = {
        ...item,
        // Explicitly omit listId to add to "all items"
      };

      return new Promise<void>((resolve, reject) => {
        addItemMutation.mutate(itemWithoutList, {
          onSuccess: () => resolve(),
          onError: (error) => reject(error),
        });
      });
    },
    [wishlistContext, addItemMutation]
  );

  return {
    addToWishlist,
    isLoading: addItemMutation.isPending,
  };
}
